import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAngleDown } from '@fortawesome/free-solid-svg-icons'
import Button from './button'
import { motion, AnimatePresence } from 'framer-motion'

interface ISelectItem {
  title: string
  value: string
}

interface ISelectProps {
  items: ISelectItem[]
  onChange: (value: string) => void
  defaultValue?: string
  value?: string
  style?: React.CSSProperties
  skeleton?: boolean
}

const Select: React.FC<ISelectProps> = ({
  value,
  defaultValue,
  onChange,
  items,
  ...rest
}) => {
  const [activeItemIndex, setActiveItemIndex] = React.useState<number>(-1)
  const [isOpen, setIsOpen] = React.useState<boolean>(false)

  const node = React.useRef<HTMLDivElement>(null)

  const handleKeyDown = React.useCallback((e: any) => {
    switch (e.keyCode) {
      case 38:
        e.preventDefault()
        setActiveItemIndex((index) => {
          return !index ? 0 : index - 1
        })
        break
      case 40:
        e.preventDefault()
        setActiveItemIndex((index) => {
          return index + 1 === items.length ? index : index + 1
        })
        break
    }
  }, [])

  const handleClick = React.useCallback((e: any) => {
    if (!node.current || node.current.contains(e.target)) {
      return
    }

    setIsOpen(false)
  }, [])

  React.useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('mousedown', handleClick)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('click', handleClick)
    }
  }, [handleKeyDown, handleClick])

  return (
    <div
      ref={node}
      className='select-wrapper'
      tabIndex={rest.skeleton ? undefined : -1}
      onKeyDown={(e) => {
        switch (e.keyCode) {
          case 27:
            setIsOpen(false)
            break
          case 13:
            e.preventDefault()
            if (isOpen) {
              onChange(items[activeItemIndex].value)
              setIsOpen(false)
            } else {
              setIsOpen(true)
            }
        }
      }}
    >
      <AnimatePresence>
        {isOpen && (
          <motion.div
            transition={{ ease: 'easeInOut' }}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className='select-menu'
            {...rest}
          >
            {items.map((item, i) => (
              <div
                key={item.title}
                tabIndex={0}
                className={`select-item ${
                  activeItemIndex === i ? 'dropdown-item-active' : ''
                } ${value === item.value ? 'select-item-selected' : ''}`}
                role='button'
                onKeyDown={(e) => {
                  if (e.keyCode === 13) {
                    onChange(item.value)
                    setIsOpen(false)
                  }
                }}
                onClick={() => {
                  onChange(item.value)
                  setIsOpen(false)
                }}
              >
                {item.title}
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <Button
        onClick={() => setIsOpen(!isOpen)}
        {...rest}
        style={{ display: 'flex', alignItems: 'center', ...rest.style }}
      >
        <div className='dropdown-value'>
          {items?.find((item) => item.value === value)?.title ||
            defaultValue ||
            'Select value'}
        </div>
        <FontAwesomeIcon className='dropdown-icon' icon={faAngleDown} />
      </Button>
    </div>
  )
}

export default Select
